import colors from 'shared/colors';
import useChartValueFormatter from '@/composables/grid-page/chart/chart-value-formatter';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import { AREA, DASH, DATETIME, DOT, LINE } from '@/lib/constants';
import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { formatTooltipCard, syncReferenceLinesToAxis } from '@/composables/grid-page/chart/utils';
import { getPointInLineBounded, linearRegression } from '@/lib/charts/line-chart';
import { getRoundedValue } from '@/lib/charts/format';
import { rgbaToHex } from 'shared/lib/color';
import { useI18n } from 'vue-i18n';

export default function useGoalProgressChart(result, chart = ref({})) {
  const { t } = useI18n();
  const { goalSettings } = useGoalSettings();
  const { formatValue } = useChartValueFormatter(chart);

  const hasData = computed(() => {
    if (!result.value?.isTimeSeries) {
      return false;
    }
    return result.value.progressData?.length > 0;
  });

  const startDate = computed(() => {
    if (!result.value?.startDate) {
      return DateTime.local();
    }
    return result.value.startDate;
  });

  const endDate = computed(() => {
    if (!result.value?.endDate) {
      return DateTime.local();
    }
    return result.value.endDate;
  });

  const baseLine = computed(() => [
    { x: startDate.value.toMillis(), y: 0 },
    { x: endDate.value.toMillis(), y: goalSettings.value.yellowThreshold },
  ]);

  const expectedValue = (timestamp) => getRoundedValue(getPointInLineBounded(baseLine.value, timestamp).y, 2);

  const calculateExpectedValue = (timestamp, start, end) => {
    const line = [
      { x: start.toMillis(), y: 0 },
      { x: end.toMillis(), y: goalSettings.value.yellowThreshold },
    ];
    return getRoundedValue(getPointInLineBounded(line, timestamp).y, 2);
  };

  const progressData = computed(() => {
    if (!hasData.value) {
      return [];
    }

    const first = {
      ...baseLine.value[0],
      expectedValue: baseLine.value[0].y,
    };

    const data = result.value.progressData
      .filter((e) => e.x >= startDate.value.toMillis() && e.x <= endDate.value.toMillis())
      .map((e) => ({
        x: e.x,
        y: e.y,
        expectedValue: expectedValue(e.x),
      }))
      .sort((a, b) => (a.x <= b.x ? -1 : 1));

    if (data.length === 0) {
      return [first];
    }
    if (data[0].x > baseLine.value[0].x) {
      data.unshift(first);
    }
    return data;
  });

  const lowestProgressData = computed(() => {
    if (progressData.value.length === 0) {
      return { y: 0 };
    }
    const minY = Math.min(...progressData.value.map((pd) => pd.y));
    return progressData.value.find((pd) => pd.y === minY) || { y: 0 };
  });

  const highestProgressData = computed(() => {
    if (progressData.value.length === 0) {
      return { y: 100 };
    }
    const maxY = Math.max(...progressData.value.map((pd) => pd.y));
    return progressData.value.find((pd) => pd.y === maxY) || { y: 100 };
  });

  const trendData = computed(() => {
    if (progressData.value.length < 2) {
      return baseLine.value;
    }

    const { slope, intercept } = linearRegression(progressData.value);
    return [
      { x: startDate.value.toMillis(), y: intercept + (slope * startDate.value.toMillis()) },
      { x: endDate.value.toMillis(), y: intercept + (slope * endDate.value.toMillis()) },
    ];
  });

  const redArea = computed(() => [
    { x: startDate.value.toMillis(), y: 0 },
    { x: endDate.value.toMillis(), y: goalSettings.value.redThreshold },
  ]);

  const yellowArea = computed(() => [
    { x: startDate.value.toMillis(), y: 0 },
    { x: endDate.value.toMillis(), y: goalSettings.value.yellowThreshold - goalSettings.value.redThreshold },
  ]);

  const greenArea = computed(() => {
    let maxY = highestProgressData.value.y;
    if (maxY < 100) {
      maxY = 100;
    }

    return [
      { x: startDate.value.toMillis(), y: maxY },
      { x: endDate.value.toMillis(), y: maxY - goalSettings.value.yellowThreshold },
    ];
  });

  const chartOptions = computed(() => ({
    chart: {
      animation: false,
      events: {
        render() {
          syncReferenceLinesToAxis(this, chart.value.referenceLines);
        },
      },
    },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      x: 50,
      floating: true,
    },
    plotOptions: {
      area: {
        stacking: 'normal',
        lineWidth: 0,
      },
    },
    series: [
      {
        states: { inactive: { opacity: 1 } },
        type: AREA,
        data: greenArea.value,
        color: colors.green.base,
        fillOpacity: 0.2,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
      },
      {
        states: { inactive: { opacity: 1 } },
        type: AREA,
        data: yellowArea.value,
        color: colors.yellow.base,
        fillOpacity: 0.2,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
      },
      {
        states: { inactive: { opacity: 1 } },
        type: AREA,
        data: redArea.value,
        color: colors.red.base,
        fillOpacity: 0.2,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
        threshold: lowestProgressData.value.y < 0 ? lowestProgressData.value.y : 0,
      },
      {
        states: { inactive: { opacity: 1 } },
        type: LINE,
        dashStyle: DASH,
        data: trendData.value,
        color: colors.grey.lighten3,
        marker: { enabled: false },
        enableMouseTracking: false,
        name: t('highcharts.trend'),
      },
      {
        type: LINE,
        data: progressData.value,
        clip: false,
        color: rgbaToHex(colors.grey.darken2),
        connectNulls: true,
        showInLegend: false,
      },
    ],
    time: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    xAxis: {
      lineColor: rgbaToHex(colors.grey.lighten3),
      lineWidth: 1,
      type: DATETIME,
      minTickInterval: 28 * 24 * 3600 * 1000,
      minRange: 28 * 24 * 3600 * 1000,
      min: startDate.value.toMillis(),
      max: endDate.value.toMillis(),
    },
    yAxis: {
      min: lowestProgressData.value.y < 0 ? lowestProgressData.value.y : 0,
      max: highestProgressData.value.y > 100 ? highestProgressData.value.y : 100,
      lineWidth: 0,
      gridLineColor: rgbaToHex(colors.grey.lighten3),
      gridLineDashStyle: DOT,
      title: { text: '' },
      labels: {
        formatter() {
          return `${this.value} %`;
        },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter() {
        const point = this.points?.find((p) => p.point.expectedValue !== undefined)?.point || this.points?.[0]?.point;
        if (!point) {
          return false;
        }
        const tag = `background:${colors.grey.lighten5};border-radius:4px;padding:1px 3px`;
        const row = 'display:flex;align-items:center;gap:.6rem';
        const date = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${DateTime.fromMillis(point.x).toFormat('LLL d, yyyy')}</div>`;
        const actual = `<div style="${row}"><span style="${tag}">${formatValue(point.y)}%</span><span style="color:${colors.grey.lighten1};margin-left:auto">${t('highcharts.actual')}</span></div>`;
        const expected = `<div style="${row}"><span style="${tag}">${formatValue(point.expectedValue)}%</span><span style="color:${colors.grey.lighten1};margin-left:auto">${t('highcharts.expected')}</span></div>`;
        return formatTooltipCard(date + actual + expected);
      },
    },
  }));

  return {
    calculateExpectedValue,
    chartOptions,
    hasData,
  };
}
